/**
 * Credential management for @sailfish-ai/autofix
 *
 * Read/write credentials to ~/.sailfish/credentials.json
 * Compatible with the desktop app's JWT format.
 */

import { readFileSync, writeFileSync, mkdirSync, existsSync, unlinkSync } from "fs";
import { dirname } from "path";
import { CREDENTIALS_FILE, CREDENTIALS_MODE } from "../constants.js";

export interface Credentials {
  jwt: string;
  graphqlUrl: string;
  updatedAt: number;
  source: "cli" | "desktop" | "web";
  /** Agent ids the user configured via `setup` — doctor scopes its checks to these. */
  agents?: string[];
}

/**
 * Get the credentials file path.
 * Prefers SAILFISH_JWT_FILE env var (for desktop app compat), falls back to default.
 */
export function getCredentialsPath(): string {
  return process.env.SAILFISH_JWT_FILE || CREDENTIALS_FILE;
}

/**
 * Read credentials from disk. Returns null if not found or invalid.
 */
export function readCredentials(): Credentials | null {
  const credPath = getCredentialsPath();
  if (!existsSync(credPath)) return null;

  try {
    const raw = JSON.parse(readFileSync(credPath, "utf-8"));
    if (!raw.jwt || !raw.graphqlUrl) return null;
    return {
      jwt: raw.jwt,
      graphqlUrl: raw.graphqlUrl,
      updatedAt: raw.updatedAt || Date.now(),
      source: raw.source || "cli",
      agents: Array.isArray(raw.agents)
        ? raw.agents.filter((a: unknown): a is string => typeof a === "string")
        : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Write credentials to disk with secure permissions.
 */
export function writeCredentials(creds: Credentials): void {
  const credPath = getCredentialsPath();
  mkdirSync(dirname(credPath), { recursive: true });
  writeFileSync(credPath, JSON.stringify(creds, null, 2), {
    mode: CREDENTIALS_MODE,
  });
}

/**
 * Record agents the user configured via `setup`, merging with any recorded
 * previously so repeated setups accumulate rather than overwrite.
 * No-op without credentials — setup authenticates before installing.
 */
export function recordConfiguredAgents(agentIds: string[]): void {
  const creds = readCredentials();
  if (!creds) return;
  const merged = [...new Set([...(creds.agents ?? []), ...agentIds])];
  writeCredentials({ ...creds, agents: merged });
}

/**
 * Delete credentials file.
 */
export function deleteCredentials(): boolean {
  const credPath = getCredentialsPath();
  if (existsSync(credPath)) {
    unlinkSync(credPath);
    return true;
  }
  return false;
}

/**
 * Check if JWT is expired (with 10-minute buffer).
 */
export function isExpired(creds: Credentials): boolean {
  try {
    const payload = JSON.parse(
      Buffer.from(creds.jwt.split(".")[1], "base64").toString()
    );
    const exp = payload.exp * 1000; // Convert to ms
    const buffer = 10 * 60 * 1000; // 10 minutes
    return Date.now() > exp - buffer;
  } catch {
    return true; // Can't parse = treat as expired
  }
}

/**
 * Check if authenticated and credentials are valid.
 */
export function isAuthenticated(): boolean {
  const creds = readCredentials();
  if (!creds) return false;
  return !isExpired(creds);
}
